import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosClient, getBaseUrlVersioned } from "../../services/axiosClient";
import { getBannerAPIUrl, getGroupedBanners } from "./utils";
const API_VERSION_APPLICATION = "v1";
export const fetchBannerDetails = createAsyncThunk("cms/banners", async (payload) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION_APPLICATION)}${getBannerAPIUrl(payload)}`;
        const response = await axiosClient.get(apiUrl);
        return response.data;
    }
    catch (error) {
        throw error;
    }
});
const initialState = {
    bannerDetails: {},
    areBannersLoaded: false,
    isLoading: false,
    audienceKey: null,
    audienceGeneration: 0,
    hasDiscardedResponse: false,
    viewedBanners: [],
    closedBanners: {},
};
export const cmsSlice = createSlice({
    name: "cms",
    initialState,
    reducers: {
        setViewedBanners: (state, action) => {
            state.viewedBanners = action.payload;
        },
        setClosedBanners: (state, action) => {
            const { screen, placement, bannerNames, } = action.payload;
            if (!state.closedBanners) {
                state.closedBanners = {};
            }
            const screenObject = state.closedBanners;
            if (!screenObject[screen]) {
                screenObject[screen] = {};
                state.closedBanners = screenObject;
            }
            const existingClosedBanners = screenObject[screen][placement] || [];
            // Merge and deduplicate banner names
            const updatedBanners = Array.from(new Set([...existingClosedBanners, ...bannerNames]));
            screenObject[screen][placement] = updatedBanners;
            state.closedBanners = screenObject;
        },
        // useCms is mounted by many components at once, so every one of them reports the audience on
        // each change. Recording the key here and comparing against it makes those reports idempotent:
        // the generation moves once per real change, not once per mounted hook.
        audienceChanged: (state, action) => {
            if (state.audienceKey === action.payload) {
                return;
            }
            const isFirstAudience = state.audienceKey === null;
            state.audienceKey = action.payload;
            if (isFirstAudience) {
                return;
            }
            state.areBannersLoaded = false;
            state.audienceGeneration += 1;
        },
        // Forced reset for callers that know the audience changed but not what it changed to.
        invalidateBanners: (state) => {
            state.areBannersLoaded = false;
            state.audienceGeneration += 1;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchBannerDetails.pending, (state) => {
            state.isLoading = true;
            state.hasDiscardedResponse = false;
        });
        builder.addCase(fetchBannerDetails.rejected, (state, action) => {
            state.isLoading = false;
            // A failure for an audience the store has left is as much a discarded response as a successful
            // one: nothing will be applied, and without a signal here no request is ever made for the
            // audience now in effect. A failure for the CURRENT audience deliberately does not set this —
            // that would retry for as long as the endpoint errors, with no backoff.
            const requestedGeneration = action.meta.arg?.audienceGeneration;
            if (requestedGeneration !== undefined && requestedGeneration !== state.audienceGeneration) {
                state.hasDiscardedResponse = true;
            }
        });
        builder.addCase(fetchBannerDetails.fulfilled, (state, action) => {
            const banners = action.payload;
            // The audience the request was made for is carried on the arg. If it has moved on since,
            // these banners belong to someone else and must not overwrite the current audience's.
            const requestedGeneration = action.meta.arg?.audienceGeneration;
            if (requestedGeneration !== undefined && requestedGeneration !== state.audienceGeneration) {
                // The audience moved on while this was in flight. Record that a response was thrown away so
                // the hook knows to fetch for the audience it now has — a failed request must NOT set this,
                // or the retry it triggers would repeat for as long as the API errors.
                state.isLoading = false;
                state.hasDiscardedResponse = true;
                return;
            }
            const groupedBanners = getGroupedBanners(banners);
            state.bannerDetails = groupedBanners;
            state.areBannersLoaded = true;
            state.isLoading = false;
        });
    },
});
export const { setViewedBanners, setClosedBanners, audienceChanged, invalidateBanners } = cmsSlice.actions;
export default cmsSlice.reducer;
